import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import WarningSignsQuiz from "@/components/WarningSignsQuiz";
import { track } from "@/lib/analytics";
import { Clock, ShieldCheck, Sparkles } from "lucide-react";

const PatternFinderPage = () => {
  const navigate = useNavigate();
  const [started, setStarted] = useState(false);

  useEffect(() => {
    document.title = "Which Pattern Is Your Boss Using? | Bad Boss Clinic";
    track("pattern_finder_viewed");
  }, []);

  const startQuiz = () => {
    track("pattern_finder_started");
    setStarted(true);
  };

  return (
    <div className="min-h-screen bg-[#020617] text-white" data-testid="pattern-finder-page">
      <header className="border-b border-slate-800/80">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-serif text-lg md:text-xl tracking-tight" data-testid="pf-logo">
            Bad Boss <span className="text-[#D4AF37]">Clinic</span>
          </Link>
          <Link
            to="/"
            className="text-slate-400 hover:text-white text-xs uppercase tracking-widest transition-colors"
            data-testid="pf-home-link"
          >
            ← Back to site
          </Link>
        </div>
      </header>

      {!started ? (
        /* Cold-traffic intro: who's behind it, what you get, how long — then one clear start button. */
        <section className="max-w-3xl mx-auto px-6 pt-12 md:pt-20 pb-24 text-center" data-testid="pattern-finder-intro">
          <span className="inline-block text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1 mb-7">
            Free · 7 questions · No sign-up
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl leading-[1.1] mb-6">
            Find out which pattern your boss is using
          </h1>
          <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-4">
            Answer 7 quick questions and get an instant read on whether your manager leans toward
            <span className="text-white"> narcissism, Machiavellianism, or psychopathy</span> — with a plain-English
            breakdown of what it means and what to do next.
          </p>
          <p className="text-slate-400 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-9">
            Built by the behavioural scientists at the Bad Boss Clinic and benchmarked against
            <span className="text-white"> 2,965 real workplace cases</span>.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-10 text-slate-400 text-sm">
            <span className="flex items-center gap-2">
              <Clock size={16} className="text-[#D4AF37]" /> About 2 minutes
            </span>
            <span className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#D4AF37]" /> Private &amp; anonymous
            </span>
            <span className="flex items-center gap-2">
              <Sparkles size={16} className="text-[#D4AF37]" /> Instant result
            </span>
          </div>

          <button
            onClick={startQuiz}
            data-testid="pattern-finder-start"
            className="bg-[#D4AF37] text-slate-950 px-10 py-4 text-sm uppercase tracking-[0.18em] font-medium hover:bg-[#B5952F] transition-colors"
          >
            Start the quiz
          </button>
          <p className="text-slate-600 text-xs mt-6">Educational only — not a clinical diagnosis.</p>
        </section>
      ) : (
        <section className="max-w-2xl mx-auto px-6 pt-10 pb-20">
          <div className="border border-slate-800 bg-slate-900/40" data-testid="pattern-finder-quiz">
            <WarningSignsQuiz onClose={() => navigate("/")} onBookSession={() => navigate("/?book=1")} />
          </div>
          <p className="text-slate-600 text-xs text-center mt-6">
            Educational only — not a clinical diagnosis. Your answers are private.
          </p>
        </section>
      )}
    </div>
  );
};

export default PatternFinderPage;
